import { ApolloError } from "@apollo/client";
import awsdk from "awsdk";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import summitLogoSmall from "/asset/image/summit-logo-small.png";
import useLazyDemographics from "/b2c/query/useLazyDemographics.b2c";
import { Alert, Button, Loading, Text } from "/component/base";
import { Menu } from "/component/base/Icon";
import MobileNavModal from "/component/modal/MobileNavModal";
import OnboardingModal from "/component/modal/OnboardingModal";
import UpdateUserPasswordModal from "/component/modal/UpdateUserPasswordModal";
import { SignUpFormType } from "/constant/signUp.constant";
import routes from "/constant/url.constant";
import { useAmwell, useAppInit, useTranslation, useViewportSize } from "/hook";
import useBookingReroute from "/hook/useBookingReroute";
import { layout } from "/styles";
import { screenSizes } from "/theme/default/breakpoints";
import { initializeAddress } from "/util/address.util";
import { setCountlyUserDetails } from "/util/countly";

import DemographicModal from "../../modal/DemographicModal";
import { Base, DesktopNavMenu, MobileNavHeader } from "./MainLayout.styles";
import { Props } from "./MainLayout.types";
import { useUserAccount } from "./useUserAccount";

const SIGN_UP_V2_ENABLED = process.env.SIGN_UP_V2_ENABLED === "true";
const LOGO_SIZE = 30;

const MainLayout = ({ children }: Props) => {
  const [navVisible, setNavVisible] = useState(false);
  const [showConfirmPasswordReset, setShowConfirmPasswordReset] = useState(false);
  const [activeToken, setToken] = useState("");
  const [demographicModal, setDemographicModal] = useState(false);
  const [facilityId, setFacilityId] = useState("");

  const { isAuthenticated, logout, isReady, loadPatientFailed, setLoadPatientFailed } =
    useAppInit();

  const [getPatientData] = useLazyDemographics();

  const { refetch } = useUserAccount();
  const { sdk, consumer, setActiveVisit } = useAmwell();
  const { t } = useTranslation("layout-MainLayout");
  const { width } = useViewportSize();
  const history = useHistory();
  const location = useLocation<{ promptCaregiver: boolean }>();
  const showMobileNav = !!width && screenSizes.tabletPortrait > width;

  useEffect(() => {
    const showPasswordReset = !!location.pathname.match(/\/passwordReset?/);
    const { token, departmentId } = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
    const departID = departmentId as string;
    setShowConfirmPasswordReset(showPasswordReset);

    if (showPasswordReset) {
      setToken(token as string);
      history.replace("/passwordReset");
    } else if (departID) {
      setFacilityId(departID);
    }
  }, []);

  const checkActiveTelehealthVisit = async () => {
    if (sdk?.initialized && consumer && isReady && isAuthenticated) {
      try {
        const activeVisit = await sdk.visitService.findActiveVisit(consumer);
        if (activeVisit) {
          setActiveVisit(activeVisit as awsdk.AWSDKVisit);
          history.push(routes.telehealthWaitingRoom);
        }
      } catch (e: unknown) {
        setActiveVisit(undefined);
        console.warn(e);
      }
    }
  };

  const handleCloseNav = () => {
    setNavVisible(false);
  };

  useEffect(() => {
    checkActiveTelehealthVisit();
  }, [isAuthenticated, isReady, consumer]);

  const handleAccountConnectionError = (e: ApolloError) => {
    if (!e.graphQLErrors || e.graphQLErrors.length === 0) {
      setLoadPatientFailed(true);
      return;
    }

    const [firstError] = e.graphQLErrors;
    const { extensions } = firstError;
    const statusCode = extensions?.status_code;

    enum ErrorCodes {
      PATIENT_NOT_FOUND = 404,
      MULTIPLE_MATCHING_MRNS = 422,
    }

    if (
      statusCode === ErrorCodes.PATIENT_NOT_FOUND ||
      statusCode === ErrorCodes.MULTIPLE_MATCHING_MRNS
    ) {
      setNavVisible(false);
      history.push(routes.accountConnectionError);
      return;
    }
  };

  const checkSignupStatus = React.useCallback(async () => {
    const { data: accountData } = await refetch();

    try {
      const { data: patientData, error } = await getPatientData();
      if (error) {
        handleAccountConnectionError(error);
      }

      const patientB2c = patientData?.patient?.demographics;
      const patientAzure = accountData?.viewer?.user.account;
      if (patientAzure) {
        const { firstName, lastName, email, phoneNumber } = patientAzure;
        setCountlyUserDetails({
          firstName,
          lastName,
          email: email ?? "",
          phoneNumber,
        });
      }
      const phoneVerificationStatus = accountData?.viewer?.user?.account?.phoneVerificationStatus;
      if (patientB2c) {
        const { address1, city, state, zipCode } = patientB2c;
        initializeAddress(address1, city, state, zipCode);
      } else if (patientAzure) {
        const { line1, city, state, postalCode } = patientAzure;
        initializeAddress(line1, city, state, postalCode);
      }

      if (SIGN_UP_V2_ENABLED) {
        if (
          phoneVerificationStatus &&
          ["notVerified", "pending"].indexOf(phoneVerificationStatus) >= 0
        ) {
          history.push(`${routes.demographics}?form=${SignUpFormType.PhoneVerification}`);
        } else if (!patientB2c && !patientAzure) {
          setDemographicModal(true);
        }
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        handleAccountConnectionError(e);
        return;
      }
      setLoadPatientFailed(true);
    }
  }, [getPatientData, refetch]);

  useEffect(() => {
    if (isAuthenticated && isReady) {
      checkSignupStatus();
    }
  }, [isAuthenticated, isReady, checkSignupStatus]);

  useBookingReroute(isReady);

  return (
    <Base>
      <MobileNavHeader>
        <Link to={routes.root}>
          <img height={LOGO_SIZE} width={LOGO_SIZE} alt={t("logo")} src={summitLogoSmall} />
        </Link>

        <Button
          css={[layout.spacedChildrenHorizontal("condensed"), layout.flexCenterHorizontal]}
          variant="unstyled"
          onClick={() => setNavVisible(true)}
        >
          <Text color="textSecondary" variant="body2Bold">
            {t("menu")}
          </Text>

          <Menu size={32} />
        </Button>
      </MobileNavHeader>

      {showMobileNav && (
        <MobileNavModal
          close={handleCloseNav}
          isAuthenticated={isAuthenticated}
          isOpen={navVisible}
          logout={logout}
        />
      )}

      <DesktopNavMenu isAuthenticated={isAuthenticated} logout={logout} />

      {!facilityId && !showConfirmPasswordReset && isReady && !isAuthenticated && (
        <OnboardingModal />
      )}

      {showConfirmPasswordReset && isReady && !isAuthenticated && (
        <UpdateUserPasswordModal
          token={activeToken}
          close={() => {
            history.replace("/home");
          }}
        />
      )}

      <Alert
        close={() => setLoadPatientFailed(false)}
        footer={
          <Button
            fullWidth="flex"
            onClick={() => {
              logout();
              setLoadPatientFailed(false);
            }}
          >
            {t("dialog.errorDialog.signOutLabel")}
          </Button>
        }
        title={t("dialog.errorDialog.title")}
        isOpen={loadPatientFailed}
      >
        {t("dialog.errorDialog.subtitle")}
      </Alert>

      {isReady ? children : <Loading />}

      <DemographicModal isOpen={demographicModal} close={() => setDemographicModal(false)} />
    </Base>
  );
};

export default MainLayout;
